// apps/vendor/src/features/support/dispatchApi.ts
import type { ComposeAttachment, ComposeFileAttachment } from '@printway/ui';
import { csrfFetch } from '../../lib/api/csrfFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface VendorDispatchRow {
  _id: string;
  parentTicketId: string;
  status: 'Dispatched' | 'InProgress' | 'Returned' | 'Cancelled';
  scopeNote: string;
  createdAt: number;
  returnedAt: number | null;
}
export interface VendorDispatchMessage {
  _id: string;
  is_vendor: boolean;
  content: string;
  createdAt: number;
  // Plan 3 Task 9b — typed attachments serialized by getVendorDispatchHandler.
  attachments?: unknown[];
}

export function useVendorDispatches(status?: string) {
  return useQuery({
    queryKey: ['vendor-dispatches', status ?? 'all'],
    queryFn: async (): Promise<VendorDispatchRow[]> => {
      const url = status ? `/api/v1/vendor/dispatches?status=${status}` : '/api/v1/vendor/dispatches';
      const res = await csrfFetch(url);
      const json = await res.json();
      if (!json.ok) throw json.error;
      return json.data.rows as VendorDispatchRow[];
    },
  });
}

export function useVendorDispatch(did: string) {
  return useQuery({
    queryKey: ['vendor-dispatch', did],
    enabled: !!did,
    queryFn: async (): Promise<{ dispatch: VendorDispatchRow; messages: VendorDispatchMessage[] }> => {
      const res = await csrfFetch(`/api/v1/vendor/dispatches/${did}`);
      const json = await res.json();
      if (!json.ok) throw json.error;
      return json.data;
    },
  });
}

/** Upload 1 file → GridFS qua ticket cha của dispatch. Không set Content-Type thủ công cho FormData. */
export async function uploadDispatchAttachment(
  parentTicketId: string,
  file: File,
): Promise<ComposeFileAttachment> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await csrfFetch(`/api/v1/tickets/${parentTicketId}/attachments`, { method: 'POST', body: form });
  const json = (await res.json()) as {
    ok: boolean;
    data?: { fileId: string; filename: string; contentType: string; size: number };
  };
  if (!res.ok || !json.ok || !json.data) throw (json && (json as { error?: unknown }).error) ?? new Error('upload_failed');
  const d = json.data;
  return { kind: 'file', fileId: d.fileId, filename: d.filename, contentType: d.contentType, size: d.size };
}

export function useReplyVendorDispatch(did: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { content: string; attachments?: ComposeAttachment[] }): Promise<void> => {
      const res = await csrfFetch(`/api/v1/vendor/dispatches/${did}/messages`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ content: payload.content, attachments: payload.attachments ?? [] }),
      });
      const json = await res.json();
      if (!json.ok) throw json.error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor-dispatch', did] });
      // F4 — first reply auto-accepts (Dispatched→InProgress); refresh the
      // list so the card status pill doesn't go stale (mirror return-work).
      qc.invalidateQueries({ queryKey: ['vendor-dispatches'] });
    },
  });
}

// Audit fix #1b — vendor trả việc (POST /vendor/dispatches/:did/return, Task 9b).
// Accept không cần nút riêng: reply đầu tiên auto-accept (Task 9).
export function useReturnVendorDispatchWork(did: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resolutionNote: string): Promise<void> => {
      const res = await csrfFetch(`/api/v1/vendor/dispatches/${did}/return`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ resolutionNote }),
      });
      const json = await res.json();
      if (!json.ok) throw json.error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor-dispatch', did] });
      qc.invalidateQueries({ queryKey: ['vendor-dispatches'] });
    },
  });
}
